/* eslint-disable max-len */
import React, { useMemo, useState } from 'react'
import { QueryClient } from 'react-query'
import { dehydrate } from 'react-query/hydration'
import { GetServerSidePropsContext } from 'next'
import Card from '@material-ui/core/Card'

import {
  MainLayout,
  MainLayoutContent,
  MainLayoutSidebar
} from 'src/features/layouts'
import {
  profilesApi,
  PROFILES_KEY,
  PROFILE_CITY_KEY,
  PROFILE_COUNTRY_KEY,
  PROFILE_TYPE,
  SPECIALIST_TAG
} from 'src/features/profiles/common'
import {
  FilterEmployersTag,
  FilterFreelanceEmployers,
  FilterOfflineEmployers,
  FilterSearchBarAutocomplete,
  FilterSearchBarMobile,
  SORT_KEY_CREATED_AT,
  SORT_KEY_RATING,
  SORT_KEY_SALARY_DOWN,
  SORT_KEY_SALARY_UP,
  TextFieldParamsSort
} from 'src/features/filters'
import {
  FilterOptionsEmployers,
  getFilterOptionsEmployers,
  removeEmptyFilterOptions,
  useFiltersStyles,
  useQueryParamsFilterEmployers
} from 'src/features/filters/common'
import {
  profileSpecialistApi,
  PROFILE_SPECIALIST_SPECIALISATIONS_KEY,
  useQueryProfileSpecialistProfessionalCategoriesFull
} from 'src/features/profile-specialist/common'
import { ProfileSpecialistList } from 'src/features/profile-specialist'
import { BreadCrumbs } from 'src/features/breadcrumbs/breadcrumbs'
//import { employersSeo, SERVICES_URL, theme } from '@plandi/common'
import {
  employersSeo,
  employersSeoEn,
  // getServiceURL,
  SERVICES_URL,
  theme,
  useTranslation
} from '@plandi/common'
import {
  SeoTextBlock,
  SEO_TEXTS,
  sharedApi,
  useQuerySeoTexts,
  TRANSLATE
} from 'src/shared'
import {
  Button,
  ButtonGroup,
  Typography,
  useMediaQuery
} from '@material-ui/core'
import { SPECIALIST_TYPE } from 'src/features/adverts/common'
import { TabsJob } from 'src/features/tabs-job'
import { CreateButtons } from 'src/features/createButtons'
import { useRouter } from 'next/router'
import { components } from '@plandi/common/src/generated'
import { SpecialistTags, useQuerySpecialistTags } from 'src/features/tags'
import { TagTextWrapper } from 'src/features/tag-text-wrapper'

export const getServerSideProps = async ({
  locale,
  query
}: GetServerSidePropsContext) => {
  const queryClient = new QueryClient()
  const queries = getFilterOptionsEmployers(query as FilterOptionsEmployers)
  const options = removeEmptyFilterOptions(queries)

  const param = {
    tag: queries.tag
  }

  await Promise.all([
    queryClient.prefetchQuery(
      [PROFILES_KEY, PROFILE_TYPE.SPECIALIST, options, locale],
      () =>
        profileSpecialistApi.getProfileSpecialistList(
          locale ?? 'en',
          'worker',
          options
        )
    ),
    queryClient.prefetchQuery(
      [PROFILE_SPECIALIST_SPECIALISATIONS_KEY, locale],
      () =>
        profileSpecialistApi.getProfileSpecialistSpecializations(locale ?? 'en')
    ),
    queryClient.prefetchQuery([PROFILE_COUNTRY_KEY, locale], () =>
      profilesApi.getProfileCountries(locale ?? 'en')
    ),
    queryClient.prefetchQuery([PROFILE_CITY_KEY, locale], () =>
      profilesApi.getProfileCities(locale ?? 'en')
    ),
    queryClient.prefetchQuery([SEO_TEXTS, locale], () =>
      sharedApi.getSEOText(locale ?? 'en')
    ),
    queryClient.prefetchQuery(TRANSLATE, () =>
      sharedApi.getTranslate(locale ?? 'en')
    ),
    queryClient.prefetchQuery([SPECIALIST_TAG, locale], () =>
      profileSpecialistApi.getSpecialistTags(locale ?? 'en')
    )
  ])

  const tags = await profileSpecialistApi.getSpecialistTags(locale ?? 'en')
  const currentTag = param.tag
    ? tags.find((tag) => tag.id === Number(param.tag))
    : null

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      locale: locale,
      currentTag
    }
  }
}

const EmployersPage = (props: {
  locale: string
  currentTag?: components['schemas']['ShopTagResource']
}) => {
  const { data: translate } = useTranslation()
  const router = useRouter()
  const url = SERVICES_URL
  const locale = props.locale
  const [itemsView, setItemsView] = useState<'sausage' | 'card'>('card')
  const [kind, setKind] = useState<string>(
    (router?.query?.specialistType as string) ?? 'freelancer'
  )
  //  const [kind, setKind] = useState<string>('freelancer')
  const [counter, setCounter] = useState<number | undefined>(0)

  const classes = useFiltersStyles()
  const { data: autocompleteSpecs } =
    useQueryProfileSpecialistProfessionalCategoriesFull()
  const sortKeys = [
    SORT_KEY_RATING,
    SORT_KEY_CREATED_AT,
    SORT_KEY_SALARY_UP,
    SORT_KEY_SALARY_DOWN
  ]
  const { data: seoTexts } = useQuerySeoTexts()
  const { query } = useQueryParamsFilterEmployers()
  const { data: tags } = useQuerySpecialistTags()
  const { title, description } = employersSeo
  const { titleEn, descriptionEn } = employersSeoEn
  const isTablet = useMediaQuery(theme.breakpoints.down(960))
  const isLaptop = useMediaQuery(theme.breakpoints.down(1441))
  const isHideButtons = useMediaQuery(theme.breakpoints.between(961, 1045))
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

  const handleKindChange = (value: string) => setKind(value)

  const tagId = Number(query.tag)

  const changedTag = useMemo(() => {
    if (!tagId) return null
    if (tagId === props.currentTag?.id) return props.currentTag
    return tags?.find((tag) => tag.id === tagId)
  }, [props.currentTag, tagId, tags])

  const getPageOne = () => {
    router.push(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          page: 1
        }
      },
      undefined,
      { shallow: true }
    )
  }

  return (
    <MainLayout
      filterSidebar={
        kind === 'freelancer' ? (
          <FilterFreelanceEmployers
            className={'modalStyles'}
            translate={translate}
            locale={props.locale}
          />
        ) : (
          <FilterOfflineEmployers
            className={'modalStyles'}
            translate={translate}
            locale={props.locale}
          />
        )
      }
      // withSearchBar
      className="grayColor"
      withFooter
      title={changedTag?.seoTitle ?? props.locale === 'ru' ? title : titleEn}
      description={
        changedTag?.seoDescription ?? props.locale === 'ru'
          ? description
          : descriptionEn
      }
      currentTag={props?.currentTag}
    >
      <div style={{ width: '100%' }}>
        <BreadCrumbs
          breadcrumbInfo={[
            {
              title: translate?.['job']?.['title2'] ?? 'Фриланс и Работа',
              href:
                locale === 'ru'
                  ? `${url.job}/ru/applicants`
                  : `${url.job}/applicants`
            },
            { title: translate?.specialists ?? 'Специалисты', href: '' }
          ]}
          styles={{ marginBottom: isDesktop ? 0 : 20 }}
        />
        <TabsJob
          place="specialists"
          translate={translate}
          locale={props.locale}
          title={changedTag ?? props.currentTag}
        />
        <SpecialistTags kind={kind} />
        {isDesktop ? (
          <>
            <FilterSearchBarAutocomplete
              withButton
              style="modalStyle"
              typeForSearch="specialist"
              translate={translate}
              autoCompleteOptions={
                autocompleteSpecs?.map((spec) => spec.name) as string[]
              }
            />
          </>
        ) : (
          <FilterSearchBarMobile
            typeForSearch={'specialist'}
            translate={translate}
          >
            {kind === 'freelancer' ? (
              <FilterFreelanceEmployers
                className={'modalStyles'}
                translate={translate}
                locale={props.locale}
              />
            ) : (
              <FilterOfflineEmployers
                className={'modalStyles'}
                translate={translate}
                locale={props.locale}
              />
            )}
          </FilterSearchBarMobile>
        )}
        <div style={{ display: 'flex' }}>
          <div>
            {isDesktop && (
              <Typography className={classes.modelAmountE}>
                {`${
                  translate?.shop?.['models-page']?.['count-results'] ??
                  'Результатов'
                }: ${counter}`}
              </Typography>
            )}
            <MainLayoutSidebar hideMobile isFixed>
              <CreateButtons translate={translate} locale={props.locale} />
              <Card>
                {kind === 'freelancer' ? (
                  <FilterFreelanceEmployers
                    translate={translate}
                    locale={props.locale}
                  />
                ) : (
                  <FilterOfflineEmployers
                    translate={translate}
                    locale={props.locale}
                  />
                )}
              </Card>
            </MainLayoutSidebar>
          </div>
          <MainLayoutContent>
            <FilterEmployersTag translate={translate} locale={props.locale} />
            {isTablet && (
              <>
                <CreateButtons translate={translate} locale={props.locale} />
                <ButtonGroup color="primary" style={{ marginBottom: 20 }}>
                  <Button
                    onClick={() => {
                      getPageOne()
                      handleKindChange(SPECIALIST_TYPE.FREELANCER)
                    }}
                    variant={kind === 'freelancer' ? 'contained' : 'outlined'}
                    style={{ textTransform: 'none' }}
                  >
                    {translate?.['job-site']?.['specialists-page']
                      ?.freelancer ?? 'Фрилансеры'}
                  </Button>
                  <Button
                    onClick={() => {
                      getPageOne()
                      handleKindChange(SPECIALIST_TYPE.WORKER)
                    }}
                    variant={kind === 'worker' ? 'contained' : 'outlined'}
                    style={{ textTransform: 'none' }}
                  >
                    {translate?.['job-site']?.['specialists-page']?.[
                      'permanent-job'
                    ] ?? 'Постоянная работа'}
                  </Button>
                </ButtonGroup>
                <Typography className={classes.modelAmountE}>
                  {`${
                    translate?.shop?.['models-page']?.['count-results'] ??
                    'Результатов'
                  }: ${counter}`}
                </Typography>
              </>
            )}
            {isDesktop && (
              <div className={classes.paramsSortContainer}>
                <ButtonGroup color="primary">
                  <Button
                    onClick={() => {
                      getPageOne()
                      handleKindChange(SPECIALIST_TYPE.FREELANCER)
                    }}
                    variant={kind === 'freelancer' ? 'contained' : 'outlined'}
                    style={{ textTransform: 'none' }}
                  >
                    {translate?.['job-site']?.['specialists-page']
                      ?.freelancer ?? 'Фрилансеры'}
                  </Button>
                  <Button
                    onClick={() => {
                      getPageOne()
                      handleKindChange(SPECIALIST_TYPE.WORKER)
                    }}
                    variant={kind === 'worker' ? 'contained' : 'outlined'}
                    style={{ textTransform: 'none' }}
                  >
                    {translate?.['job-site']?.['specialists-page']?.[
                      'permanent-job'
                    ] ?? 'Постоянная работа'}
                  </Button>
                </ButtonGroup>
                <TextFieldParamsSort
                  sortKeys={sortKeys}
                  isEmployersPage
                  isHaveButtonGroup
                  setItemsView={setItemsView}
                  defaultValue={SORT_KEY_RATING}
                  itemsView={itemsView}
                  translate={translate}
                  typeForSearch={'specialist'}
                  style={{
                    width: isHideButtons ? 180 : isLaptop ? '100%' : 384,
                    marginBottom: 0,
                    marginTop: isTablet ? 15 : 0
                  }}
                />
              </div>
            )}
            <ProfileSpecialistList
              itemsView={itemsView}
              type={kind}
              changeCounter={setCounter}
              translate={translate}
              locale={props.locale}
            />
          </MainLayoutContent>
        </div>
        {tagId &&
        (changedTag?.description || props?.currentTag?.description) ? (
          <TagTextWrapper
            text={changedTag?.description ?? props?.currentTag?.description}
          />
        ) : (
          <SeoTextBlock text={seoTexts?.specialist} />
        )}
      </div>
    </MainLayout>
  )
}

export default EmployersPage
